import { Campaign } from "@/types/campaign";

/**
 * Determines the appropriate redirect URL based on campaign status
 * @param campaign - The campaign object
 * @param hasPackages - Whether the campaign has packages available
 * @returns The redirect URL for the campaign
 */
export function getCampaignRedirectUrl(campaign: Campaign, hasPackages: boolean = false): string {
  const campaignId = campaign.id;
  
  switch (campaign.status) {
    case 'draft':
      // Draft campaigns should go to campaign detail page for editing
      return `/campaigns/${campaignId}`;
      
    case 'submitted':
      // Submitted campaigns should show packages if available, otherwise browse eligible projects
      if (hasPackages) {
        return `/campaigns/${campaignId}/packages`;
      }
      return `/campaigns/${campaignId}/browse-eligible`;
      
    case 'packaging':
      // Packaging campaigns should show packages if available, otherwise projects being packaged
      if (hasPackages) {
        return `/campaigns/${campaignId}/packages`;
      }
      return `/campaigns/${campaignId}/projects`;
      
    case 'presented':
      // Presented campaigns should show packages for review if available
      if (hasPackages) {
        return `/campaigns/${campaignId}/packages`;
      }
      return `/campaigns/${campaignId}/browse-eligible`;
      
    case 'hold':
      // Hold campaigns should show hold placed page
      return `/campaigns/${campaignId}/hold-placed`;
      
    case 'confirmed':
      // Confirmed campaigns should show package ready page
      return `/campaigns/${campaignId}/package-ready`;
      
    case 'active':
      // Active campaigns should show packages if available, otherwise projects/performance
      if (hasPackages) {
        return `/campaigns/${campaignId}/packages`;
      }
      return `/campaigns/${campaignId}/projects`;
      
    case 'delivered':
      // Delivered campaigns should show final projects/reports
      return `/campaigns/${campaignId}/projects`;
      
    case 'complete':
      // Complete campaigns should show final reports
      return `/campaigns/${campaignId}/projects`;
      
    case 'cancelled':
      // Cancelled campaigns should show campaign details
      return `/campaigns/${campaignId}`;
      
    default:
      // Fallback to browse eligible projects
      return `/campaigns/${campaignId}/browse-eligible`;
  }
}

/**
 * Gets the appropriate action text for the campaign button based on status
 * @param campaign - The campaign object
 * @param hasPackages - Whether the campaign has packages available
 * @returns The button text
 */
export function getCampaignActionText(campaign: Campaign, hasPackages: boolean = false): string {
  switch (campaign.status) {
    case 'draft':
      return 'Edit Campaign';
      
    case 'submitted':
      return hasPackages ? 'Review Packages' : 'Browse Projects';
      
    case 'packaging':
      return hasPackages ? 'Review Packages' : 'View Packaging';
      
    case 'presented':
      return hasPackages ? 'Review Packages' : 'Browse Projects';
      
    case 'hold':
      return 'View Hold Status';
      
    case 'confirmed':
      return 'View Package';
      
    case 'active':
      return hasPackages ? 'Review Packages' : 'View Performance';
      
    case 'delivered':
      return 'View Results';
      
    case 'complete':
      return 'View Final Report';
      
    case 'cancelled':
      return 'View Details';
      
    default:
      return 'View Campaign';
  }
}